import { api } from "@/lib/apiClient";
import { formatQuantityWithUnit } from "@/lib/documentCalc";
import {
  userHasGroup,
  userIsAbrechnung,
  userIsBuero,
  userIsManagement,
  type CurrentUser,
} from "@/lib/auth";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { readGdMeta } from "@/pages/dokumente/gdPositionMeta";
import type { EditorPosition } from "@/pages/dokumente/documentPositionModel";

function normLabel(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Findet die Dokumentposition zu einer Timesheet-Materialzeile.
 * Reihenfolge: Meta-Stamp → quelle_position_id → Bezeichnung.
 */
export function matchPositionForTimesheetMaterial(
  positions: EditorPosition[],
  material: TimesheetMaterialRecord | null | undefined,
  materialPositionId?: number | null,
): EditorPosition | undefined {
  const mid =
    materialPositionId != null && Number.isFinite(Number(materialPositionId))
      ? Number(materialPositionId)
      : material?.id != null && Number.isFinite(Number(material.id))
        ? Number(material.id)
        : null;

  if (mid != null) {
    const byMeta = positions.find(
      (pos) => readGdMeta(pos.kalkulation_json).timesheet_material_position_id === mid,
    );
    if (byMeta) return byMeta;
  }

  const gdPosId =
    material?.gd_position_id != null && Number.isFinite(Number(material.gd_position_id))
      ? Number(material.gd_position_id)
      : null;
  if (gdPosId != null) {
    const byQuelle = positions.find(
      (pos) =>
        pos.quelle_position_id != null && Number(pos.quelle_position_id) === gdPosId,
    );
    if (byQuelle) return byQuelle;
  }

  const materialText = normLabel(material?.text);
  if (materialText) {
    const exact = positions.filter((pos) => normLabel(pos.bezeichnung) === materialText);
    if (exact.length === 1) return exact[0];

    const fuzzy = positions.filter((pos) => {
      const label = normLabel(pos.bezeichnung);
      return label.includes(materialText) || materialText.includes(label);
    });
    if (fuzzy.length === 1) return fuzzy[0];
  }

  return undefined;
}

/** Signatur der Unterpositionen zum Vergleich „neue Version“. */
export function mergedChildrenSignature(children: Array<{ bezeichnung?: string | null; menge?: number | null; einheit?: string | null; einzelpreis_netto?: number | null }>): string {
  return children
    .map((c) =>
      [
        normLabel(c.bezeichnung),
        Number(c.menge) || 0,
        normLabel(c.einheit),
        Number(c.einzelpreis_netto) || 0,
      ].join("|"),
    )
    .join("||");
}

export type DokumentBillingQuestionItem = {
  id: number;
  position_id?: number | null;
  question_text?: string | null;
  response_text?: string | null;
  price?: number | string | null;
  responded_at?: string | null;
  material_text?: string | null;
  material_menge?: number | null;
  material_einheit?: string | null;
  /** Gespeicherter Text, bleibt erhalten wenn die Position ersetzt wird. */
  position_label?: string | null;
  position_nr?: number | string | null;
  position_beschreibung?: string | null;
  hero_nr?: string | null;
  position_kalkulation_json?: Record<string, unknown> | null;
};

function cleanPositionText(value: unknown): string {
  const text = String(value || "")
    .replace(/\r/g, "\n")
    .trim();
  if (!text) return "";
  const first = text
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean);
  if (!first) return "";
  return first.length > 140 ? `${first.slice(0, 137).trimEnd()}…` : first;
}

/** Nummer, Bezeichnung und Menge – „Position“ allein ist kein Bezug. */
export function formatBillingPositionReference(input: {
  displayNumber?: string | number | null;
  bezeichnung?: string | null;
  beschreibung?: string | null;
  menge?: number | string | null;
  einheit?: string | null;
  heroNr?: string | null;
}): string {
  const numberRaw =
    input.displayNumber != null && String(input.displayNumber).trim()
      ? String(input.displayNumber).trim()
      : "";
  const number = numberRaw ? `Pos. ${numberRaw}` : "";

  let title = cleanPositionText(input.bezeichnung);
  if (title.toLowerCase() === "position") title = "";
  const description = cleanPositionText(input.beschreibung);
  const hero = cleanPositionText(input.heroNr);
  if (!title) title = description && description.toLowerCase() !== "position" ? description : hero;

  const menge =
    input.menge != null && String(input.menge).trim() !== "" && Number.isFinite(Number(input.menge))
      ? Number(input.menge)
      : null;
  const unit = String(input.einheit || "").trim();
  const qty =
    menge == null
      ? ""
      : !unit && menge === 1
        ? ""
        : formatQuantityWithUnit(menge, unit);

  return [number, title, qty].filter(Boolean).join(" · ");
}

/** Live-Angabe aus dem offenen Dokument, sonst der beim Fragen gespeicherte Text. */
export function chooseBillingPositionLabel(live: string, stored?: string | null): string {
  const current = live.trim();
  const saved = String(stored || "").trim();
  if (!current) return saved;
  if (!saved || current.includes("·")) return current;
  // Live ist nur „Pos. N“. Bezeichnung aus dem gespeicherten Text übernehmen,
  // die Nummer aus dem Dokument behalten.
  const savedName = saved.replace(/^Pos\.\s*\d+(?:\.\d+)?\s*(?:·\s*)?/, "").trim();
  if (!savedName) return current;
  return `${current} · ${savedName}`;
}

export type DokumentBillingQuestion = {
  id: number;
  dokument_id: number;
  created_by_login?: string | null;
  assigned_to_group?: string | null;
  status?: string | null;
  question_text?: string | null;
  response_text?: string | null;
  created_at?: string | null;
  responded_at?: string | null;
  items?: DokumentBillingQuestionItem[];
};

/** Büro ohne Geschäftsführung (+ App-Admins). */
export function canCreateDocumentBillingQuestions(user: CurrentUser | null): boolean {
  if (!user) return false;
  if (userHasGroup(user, "App-Admins")) return true;
  if (userIsManagement(user)) return false;
  return userIsBuero(user);
}

export function canViewDocumentBillingAnswers(user: CurrentUser | null): boolean {
  if (!user) return false;
  return (
    userIsAbrechnung(user) ||
    userIsBuero(user) ||
    userIsManagement(user) ||
    userHasGroup(user, "App-Admins")
  );
}

export function canRespondDocumentBillingQuestions(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userIsManagement(user) || userHasGroup(user, "App-Admins");
}

/** Hinweis an einer Rückfrage, die von der verknüpften Rechnung kommt. */
export function linkedInvoiceRueckfrageLabel(invoiceNumber: string): string {
  const number = invoiceNumber.trim();
  if (!number) return "aus verknüpfter Rechnung";
  return `aus verknüpfter Rechnung Nr. ${number}`;
}

/** Hinweiszeile über offenen Rückfragen im Stundennachweis. */
export function openBillingQuestionBanner(
  timesheetOpenCount: number,
  invoiceOpenCount: number,
  invoiceNumber: string,
): string {
  const total = Math.max(0, timesheetOpenCount) + Math.max(0, invoiceOpenCount);
  const base =
    total === 1
      ? "1 offene Rückfrage zur Abrechnung"
      : `${total} offene Rückfragen zur Abrechnung`;
  if (invoiceOpenCount <= 0) return base;
  const label = linkedInvoiceRueckfrageLabel(invoiceNumber);
  if (timesheetOpenCount <= 0) return `${base} ${label}`;
  return `${base}, ${label}`;
}

export function dokumentBillingQuestionIsOpen(q: DokumentBillingQuestion): boolean {
  const generalQuestion = String(q.question_text || "").trim();
  const generalAnswer = String(q.response_text || "").trim();
  if (generalQuestion && !generalAnswer) return true;
  const items = q.items || [];
  if (!items.length) return !q.responded_at;
  return items.some((item) => dokumentBillingItemIsOpen(item));
}

function dokumentBillingItemIsOpen(item: DokumentBillingQuestionItem): boolean {
  return !(item.response_text || "").trim() && item.price == null;
}

export type DokumentBillingAnswerPart = {
  key: string;
  questionId: number;
  itemId: number | null;
  positionLabel: string;
  questionText: string;
};

/** Offene Antwortfelder einer Dokument-Rückfrage (allgemein und je Position). */
export function openDokumentBillingAnswerParts(
  questions: DokumentBillingQuestion[],
): DokumentBillingAnswerPart[] {
  const parts: DokumentBillingAnswerPart[] = [];
  for (const question of questions) {
    const generalQuestion = String(question.question_text || "").trim();
    const generalAnswer = String(question.response_text || "").trim();
    if (generalQuestion && !generalAnswer) {
      parts.push({
        key: `q-${question.id}`,
        questionId: question.id,
        itemId: null,
        positionLabel: "Allgemein",
        questionText: generalQuestion,
      });
    }
    for (const item of question.items || []) {
      if (!dokumentBillingItemIsOpen(item)) continue;
      const live = formatBillingPositionReference({
        displayNumber: item.position_nr,
        bezeichnung: item.material_text,
        beschreibung: item.position_beschreibung,
        menge: item.material_menge,
        einheit: item.material_einheit,
        heroNr: item.hero_nr,
      });
      parts.push({
        key: `item-${item.id}`,
        questionId: question.id,
        itemId: item.id,
        positionLabel: chooseBillingPositionLabel(live, item.position_label) || "Position",
        questionText: String(item.question_text || "").trim() || "Rückfrage zur Position",
      });
    }
  }
  return parts;
}

export type DokumentMitOffenenRueckfragen = {
  dokument_id: number;
  dokument_typ?: string | null;
  display_number?: string | null;
  betreff?: string | null;
  status?: string | null;
  auftrag_id?: number | null;
  auftrag_display?: string | null;
  kunde?: string | null;
  billing_question_open_count?: number | null;
  latest_question_at?: string | null;
};

/** Dokumente (Rechnungen, Lieferscheine, …) mit offenen Abrechnungs-Rückfragen. */
export async function listDokumenteWithOpenBillingQuestions(): Promise<
  DokumentMitOffenenRueckfragen[]
> {
  const data = await api.get<DokumentMitOffenenRueckfragen[]>(
    "/dokumente/billing-questions/open",
  );
  return Array.isArray(data) ? data : [];
}

export function dokumentTypLabel(typ?: string | null): string {
  switch (String(typ || "").toLowerCase()) {
    case "rechnung":
      return "Rechnung";
    case "lieferschein":
      return "Lieferschein";
    case "angebot":
      return "Angebot";
    case "auftragsbestaetigung":
      return "Auftragsbestätigung";
    case "gutschrift":
      return "Gutschrift";
    default:
      return "Dokument";
  }
}

export async function listDokumentBillingQuestions(
  dokumentId: number,
  options?: { includeAnswered?: boolean },
): Promise<DokumentBillingQuestion[]> {
  const qs = options?.includeAnswered ? "?include_answered=true" : "";
  return api.get(`/dokumente/${dokumentId}/billing-questions${qs}`);
}

export async function createDokumentBillingQuestions(
  dokumentId: number,
  payload: {
    general_question?: string;
    position_questions?: Array<{ position_id?: number; question_text: string }>;
  },
): Promise<{ ok: boolean; question_id: number }> {
  return api.post(`/dokumente/${dokumentId}/billing-questions`, payload);
}

export async function respondDokumentBillingQuestion(
  dokumentId: number,
  questionId: number,
  payload: {
    response_text?: string;
    items?: Array<{
      item_id: number;
      response_text?: string;
      price?: number;
      einzelpreis_netto?: number;
      kalkulation_json?: Record<string, unknown> | null;
    }>;
  },
): Promise<void> {
  await api.post(`/dokumente/${dokumentId}/billing-questions/${questionId}/respond`, payload);
}

export type PositionBillingBadgeStatus = "open" | "answered";

/** Badge-Status je Dokumentpositions-localId. */
export function buildPositionBillingBadgeMap(input: {
  dokumentQuestions: DokumentBillingQuestion[];
  timesheetQuestions?: Array<{
    items?: Array<{
      material_position_id?: number | null;
      response_text?: string | null;
      price?: number | string | null;
    }>;
    response_text?: string | null;
    responded_at?: string | null;
  }>;
  timesheetMaterials?: TimesheetMaterialRecord[];
  positions: EditorPosition[];
}): Map<string, PositionBillingBadgeStatus> {
  const byLocalId = new Map<string, PositionBillingBadgeStatus>();

  const setStatus = (localId: string, status: PositionBillingBadgeStatus) => {
    const prev = byLocalId.get(localId);
    if (prev === "open") return;
    byLocalId.set(localId, status);
  };

  const positionById = new Map<number, EditorPosition>();
  for (const pos of input.positions) {
    if (pos.id != null && Number.isFinite(Number(pos.id))) {
      positionById.set(Number(pos.id), pos);
    }
  }

  const materials = input.timesheetMaterials || [];

  for (const q of input.dokumentQuestions) {
    const items = q.items || [];
    if (!items.length) continue;
    for (const item of items) {
      if (item.position_id == null) continue;
      const pos = positionById.get(Number(item.position_id));
      if (!pos) continue;
      const open = !(item.response_text || "").trim() && item.price == null;
      setStatus(pos.localId, open ? "open" : "answered");
    }
  }

  for (const q of input.timesheetQuestions || []) {
    for (const item of q.items || []) {
      if (item.material_position_id == null) continue;
      const mid = Number(item.material_position_id);
      const material = materials.find((m) => Number(m.id) === mid);
      const pos = matchPositionForTimesheetMaterial(input.positions, material, mid);
      if (!pos) continue;
      const open = !(item.response_text || "").trim() && item.price == null;
      setStatus(pos.localId, open ? "open" : "answered");
    }
  }

  return byLocalId;
}

/**
 * Hängt Rückfragen nach einem Positions-Replace an die neuen Zeilen-IDs.
 * ``previousIds`` und ``nextIds`` stehen in der gespeicherten Reihenfolge.
 */
export function remapDokumentBillingQuestionPositionIds(
  questions: DokumentBillingQuestion[],
  previousIds: Array<number | null | undefined>,
  nextIds: Array<number | null | undefined>,
): DokumentBillingQuestion[] {
  const oldToNew = new Map<number, number>();
  const count = Math.min(previousIds.length, nextIds.length);
  for (let index = 0; index < count; index += 1) {
    const oldRaw = previousIds[index];
    const newRaw = nextIds[index];
    if (oldRaw == null || newRaw == null) continue;
    const oldId = Number(oldRaw);
    const newId = Number(newRaw);
    if (!Number.isFinite(oldId) || !Number.isFinite(newId) || oldId === newId) continue;
    if (!oldToNew.has(oldId)) oldToNew.set(oldId, newId);
  }
  if (oldToNew.size === 0) return questions;

  let changed = false;
  const next = questions.map((question) => {
    const items = question.items || [];
    let itemsChanged = false;
    const mappedItems = items.map((item) => {
      if (item.position_id == null) return item;
      const mappedId = oldToNew.get(Number(item.position_id));
      if (mappedId == null) return item;
      itemsChanged = true;
      changed = true;
      return { ...item, position_id: mappedId };
    });
    return itemsChanged ? { ...question, items: mappedItems } : question;
  });
  return changed ? next : questions;
}

/** Neue DB-IDs nach Replace, ohne lokale Edits an der Position zu überschreiben. */
export function adoptReplacedPositionIds<T extends { localId: string; id?: number | null }>(
  rows: T[],
  savedLocalIds: string[],
  savedPositions: Array<{ id?: number | null }>,
): T[] {
  const nextIdByLocalId = new Map<string, number>();
  const count = Math.min(savedLocalIds.length, savedPositions.length);
  for (let index = 0; index < count; index += 1) {
    const localId = savedLocalIds[index];
    const id = savedPositions[index]?.id;
    if (!localId || id == null || !Number.isFinite(Number(id))) continue;
    nextIdByLocalId.set(localId, Number(id));
  }
  if (nextIdByLocalId.size === 0) return rows;

  let changed = false;
  const next = rows.map((row) => {
    const id = nextIdByLocalId.get(row.localId);
    if (id == null || Number(row.id) === id) return row;
    changed = true;
    return { ...row, id };
  });
  return changed ? next : rows;
}
